<!-- eslint-disable vue/no-root-v-if -->
<script setup>
// @ts-check

import { useAttachment } from '@/core/composables/useAttachment'
import { postNativeMessage } from '@/helpers/mobile'
import { computed, toRefs } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  attachment: {
    /** @type {import('vue').PropType<Pick<AttachmentResource, 'mime_type'|'original_file_name'|'file'|'file_thumbnail'>>} */
    type: Object,
    required: true
  },
  // Medium, small
  size: {
    type: String,
    default: 'medium'
  }
})

const emit = defineEmits(['clickImage'])

defineOptions({
  name: 'FileThumbnail'
})

const store = useStore()
const { attachment } = toRefs(props)
const { isImage, isXlsx, isPdf } = useAttachment(attachment)

const isMobileWrapperUser = computed(
  () => store.getters['mobileData/isMobileWrapperUser']
)

const fileThumbnailSrc = computed(() => {
  if (attachment.value?.file_thumbnail) {
    return `data:image/jpg;base64,${attachment.value.file_thumbnail}`
  }

  if (isImage.value) {
    return `data:${attachment.value.mime_type};base64,${attachment.value.file}`
  }

  return null
})

/**
 * @param base64Str {string}
 */
const base64ToArrayBuffer = (base64Str) => {
  const binaryString = window.atob(base64Str)
  const binaryLen = binaryString.length
  const bytes = new Uint8Array(binaryLen)
  for (let i = 0; i < binaryLen; i += 1) {
    const ascii = binaryString.charCodeAt(i)
    bytes[i] = ascii
  }
  return bytes
}

const openFileInNewTab = () => {
  const byte = base64ToArrayBuffer(attachment.value.file)
  const blob = new Blob([byte], { type: attachment.value.mime_type })
  window.open(URL.createObjectURL(blob), '_blank')
}

const onPdfClick = () => {
  if (isMobileWrapperUser.value) {
    const message = JSON.stringify({
      type: 'ukkopro_file_action',
      message: {
        fileData: attachment.value.file,
        name: attachment.value.original_file_name,
        action: 'open',
        type: 'application/pdf'
      }
    })
    postNativeMessage(message)
  } else {
    openFileInNewTab()
  }
}

const onXlsxClick = () => {
  openFileInNewTab()
}

const onImageClick = () => {
  emit('clickImage')
}

const isSmall = computed(() => props.size === 'small')

const iconSizeAttr = computed(() => {
  return isSmall.value ? '2.25rem' : '4rem'
})

const handleClick = () => {
  if (isImage.value) {
    onImageClick()
  } else if (isPdf.value) {
    onPdfClick()
  } else if (isXlsx.value) {
    onXlsxClick()
  }
}
</script>

<template>
  <button
    v-if="isImage || isXlsx || isPdf"
    type="button"
    class="flex"
    @click="handleClick"
  >
    <img
      v-if="fileThumbnailSrc"
      class="object-contain"
      :class="{
        'h-full w-9': $slots.extra,
        'size-full': !$slots.extra
      }"
      :title="attachment.original_file_name"
      :src="fileThumbnailSrc"
      :alt="attachment.original_file_name"
    />

    <span
      v-else-if="isXlsx || isPdf"
      class="relative flex items-center justify-center overflow-hidden"
      :class="{
        'h-full w-9': $slots.extra,
        'size-full': !$slots.extra
      }"
      :title="attachment.original_file_name"
    >
      <VIcon
        icon="$file-outline"
        :size="iconSizeAttr"
        class="bg-transparent fill-current text-primary"
      />
      <span
        class="pointer-events-none absolute top-1/2 left-1/2 mt-1.5 -translate-x-1/2 -translate-y-1/2 bg-primary font-medium text-white"
        :class="{
          'px-0.5 text-[0.5rem]': isSmall,
          'px-1 text-xs': !isSmall
        }"
      >
        {{ isPdf ? 'PDF' : 'Excel' }}
      </span>
    </span>
    <slot name="extra" />
  </button>
</template>
